/**
 * Local database.
 *
 * `eventos` is the device's own copy of the log — its own un-synced events plus everything
 * pulled from the server. Every other table is a projection rebuilt from it, so none of them
 * is ever the truth and none needs to be migrated carefully.
 */

import Dexie, { type Table } from 'dexie';
import { nombreBaseDatos } from '../lib/practica.js';
import type { Envelope } from '@abastece/shared';
import type {
  Abono,
  Incobrable,
  Deposito,
  TransferenciaBanco,
  Cliente,
  Compra,
  Devolucion,
  DevolucionCompra,
  Dispositivo,
  Empleado,
  Existencia,
  Gasto,
  Jornada,
  MovimientoDueno,
  Oferta,
  PagoAguinaldo,
  PagoCotizaciones,
  Planilla,
  ListaPrecio,
  Lote,
  Pedido,
  Precio,
  Conteo,
  Producto,
  Proveedor,
  Recarga,
  Reempaque,
  Salida,
  SaldoTelefonico,
  SesionCaja,
  Traslado,
  UnidadVenta,
  Usuario,
  Venta,
} from '@abastece/core';

export type EstadoEvento = 'pendiente' | 'confirmado' | 'rechazado';

export interface LocalEvent extends Envelope {
  /** Set once the server has sequenced it. Absent means it is still only on this device. */
  serverSeq?: number;
  serverTs?: string;
  estado: EstadoEvento;
  motivoRechazo?: string;
  /**
   * Refused by this device's own books as it was recorded, on a screen that said so and did not
   * carry on (`emitirYVer`): it never happened, and is never sent. Not something to review — the
   * person saw it refused — so it is left out of what «Ajustes» and the status line count.
   */
  rechazadoAqui?: boolean;
}

export interface ConfigRow {
  clave: string;
  valor: unknown;
  rev: number;
}

export interface AsientoRow {
  id?: number;
  eventId: string;
  fecha: string;
  concepto: string;
  lineas: { cuenta: string; debe: number; haber: number; memo?: string; dolares?: number }[];
}

/**
 * One account's journal lines on one day, added up: what the reports and the cash screens read
 * instead of every entry (see `sumarAlDia` in `db/effects.ts`). Written with every entry and taken
 * back with it, and nowhere in the core: the server's books answer the same questions in SQL.
 */
export interface SaldoDiario {
  /** `saldoDiarioId(cuenta, fecha)`. */
  id: string;
  cuenta: string;
  fecha: string;
  debe: number;
  haber: number;
  /** How many lines: a stock account's rounding reaches half a centavo per line (`cuadres`). */
  lineas: number;
  /**
   * A dollar account's dollars that day, USD cents, those in less those out (`JournalLine.dolares`):
   * what it holds in dollars is these added up. Absent on any other account's days.
   */
  dolares?: number;
}

export const saldoDiarioId = (cuenta: string, fecha: string): string => `${cuenta}|${fecha}`;

export interface MetaRow {
  clave: string;
  valor: unknown;
}

/**
 * One row an applied event changed, as it was before: `antes` is null when the event created it.
 * A journal entry has an autoincrement key, so it is kept by that key and taken back by deleting.
 */
export type Cambio =
  | { tipo: 'fila'; tabla: string; clave: string; antes: unknown }
  | { tipo: 'asiento'; id: number };

/** A sale as this device keeps it, with the fields its indexes need (see `indicesDeVenta`). */
export type VentaLocal = Venta & { enCola?: 0 | 1; conReferencia?: 0 | 1 };

/** How to take back one applied event (see `db/aplicar.ts`). `orden` is the order it was applied in. */
/**
 * An event this device's books took nothing from: refused as it was applied, here or in a replay,
 * with the books' reason (`db/aplicar.ts`). The log keeps every event, the refused ones too, and
 * the server sends them like any other; a screen that counts events — «Control», the payments made,
 * the counts of the safe — leaves these out (`lib/sinEfecto.ts`). «Control» counted a sale voided
 * twice as two voids, and a refused handover moved the drawer to the wrong person (2026-10-13).
 */
export interface SinEfectoRow {
  eventId: string;
  motivo: string;
}

export interface DeshacerRow {
  orden?: number;
  eventId: string;
  /** The server's number it was applied under; absent when it had none yet. */
  serverSeq?: number;
  cambios: Cambio[];
}

/**
 * Where a photo's bytes stand on this device.
 *
 *   pendiente   taken here and not yet on the server
 *   subida      taken here, and the server has it
 *   bajada      taken somewhere else and fetched from the server
 *   rechazada   taken here, and the server will never take it — kept, because this device can
 *               still show it, but no longer retried
 */
export type EstadoFoto = 'pendiente' | 'subida' | 'bajada' | 'rechazada';

export interface FotoLocal {
  /** SHA-256 of `datos`. The same name the product event carries. */
  huella: string;
  tipo: string;
  datos: Blob;
  estado: EstadoFoto;
  creadaEn: string;
}

class AbasteceDB extends Dexie {
  eventos!: Table<LocalEvent, string>;
  usuarios!: Table<Usuario, string>;
  dispositivos!: Table<Dispositivo, string>;
  config!: Table<ConfigRow, string>;
  asientos!: Table<AsientoRow, number>;
  saldosDiarios!: Table<SaldoDiario, string>;
  transferenciasBanco!: Table<TransferenciaBanco, string>;
  meta!: Table<MetaRow, string>;
  fotos!: Table<FotoLocal, string>;
  deshacer!: Table<DeshacerRow, number>;
  sinEfecto!: Table<SinEfectoRow, string>;

  productos!: Table<Producto, string>;
  unidades!: Table<UnidadVenta, string>;
  listas!: Table<ListaPrecio, string>;
  precios!: Table<Precio, string>;
  proveedores!: Table<Proveedor, string>;
  existencias!: Table<Existencia, string>;
  lotes!: Table<Lote, string>;
  compras!: Table<Compra, string>;
  sesiones!: Table<SesionCaja, string>;
  ventas!: Table<VentaLocal, string>;
  saldos!: Table<SaldoTelefonico, string>;
  recargas!: Table<Recarga, string>;
  reempaques!: Table<Reempaque, string>;
  conteos!: Table<Conteo, string>;
  salidas!: Table<Salida, string>;
  traslados!: Table<Traslado, string>;
  clientes!: Table<Cliente, string>;
  pedidos!: Table<Pedido, string>;
  jornadas!: Table<Jornada, string>;
  gastos!: Table<Gasto, string>;
  movimientosDueno!: Table<MovimientoDueno, string>;
  empleados!: Table<Empleado, string>;
  planillas!: Table<Planilla, string>;
  pagosCotizaciones!: Table<PagoCotizaciones, string>;
  pagosAguinaldo!: Table<PagoAguinaldo, string>;
  ofertas!: Table<Oferta, string>;
  abonos!: Table<Abono, string>;
  incobrables!: Table<Incobrable, string>;
  depositos!: Table<Deposito, string>;
  devoluciones!: Table<Devolucion, string>;
  devolucionesCompra!: Table<DevolucionCompra, string>;

  constructor() {
    // Practice mode is a different database, not a different code path — see `lib/practica.ts`.
    super(nombreBaseDatos());
    this.version(1).stores({
      eventos: 'id, serverSeq, deviceSeq, estado, type',
      usuarios: 'id, nombre, activo',
      dispositivos: 'id, codigo',
      config: 'clave',
      asientos: '++id, eventId, fecha',
      meta: 'clave',
    });

    // Fase 1: catálogo, costos y compras. Projections only — all of it rebuilds from `eventos`,
    // so this upgrade needs no data migration.
    this.version(2).stores({
      productos: 'id, nombre, categoria, activo, *codigosBarras',
      unidades: 'id, productoId',
      listas: 'id, orden',
      precios: 'id, unidadVentaId, listaId',
      proveedores: 'id, nombre, activo',
      existencias: 'id, productoId, ubicacion',
      lotes: 'id, productoId, vencimiento',
      compras: 'id, proveedorId, fecha, vencePago',
    });

    // Fase 2: punto de venta. Projections again — rebuilt from `eventos`, no data migration.
    this.version(3).stores({
      sesiones: 'id, estado, dispositivoId, abiertaEn',
      ventas: 'id, ticket, sesionId, fecha, anulada',
      saldos: 'id',
      recargas: 'id, sesionId, fecha',
    });

    // Fase 3: movimientos de inventario. Projections again — rebuilt from `eventos`.
    this.version(4).stores({
      reempaques: 'id, fecha, ubicacion',
      conteos: 'id, fecha, ubicacion',
      salidas: 'id, fecha, motivo, productoId',
      traslados: 'id, fecha',
    });

    // Fase 4: clientes y reparto. Projections again — rebuilt from `eventos`.
    this.version(5).stores({
      clientes: 'id, nombre, tipo, diaVisita, activo',
      pedidos: 'id, clienteId, fechaEntrega, estado',
      jornadas: 'id, camion, fecha, estado',
    });

    // Fase 5: gastos y dinero del dueño. Projections again — rebuilt from `eventos`.
    this.version(6).stores({
      gastos: 'id, fecha, categoria, anulado',
      movimientosDueno: 'id, fecha, tipo',
    });

    // Fase 6: planilla. Projections again — rebuilt from `eventos`.
    this.version(7).stores({
      empleados: 'id, nombre, activo',
      planillas: 'id, periodo, fecha, pagada',
      pagosCotizaciones: 'id, periodo, fecha',
    });

    /**
     * Product photos. NOT a projection, and deliberately absent from `tablasProyectadas`: the
     * log names a photo but never holds it, so a replay that cleared this table could not put
     * a single one back — the device would be left fetching every photo again, and the ones it
     * took itself and had not uploaded yet would be gone for good.
     */
    this.version(8).stores({
      fotos: 'huella, estado',
    });

    // Fiado y devoluciones. Projections again — rebuilt from `eventos`. Sales gain a client
    // index, because a client's statement is every sale made to them.
    this.version(9).stores({
      ventas: 'id, ticket, sesionId, fecha, anulada, clienteId',
      abonos: 'id, clienteId, fecha',
      devoluciones: 'id, ventaId, clienteId, fecha',
      devolucionesCompra: 'id, compraId, proveedorId, fecha',
    });

    /**
     * How to take back the events this device applied before the server placed them. Not a
     * projection, and not in `tablasProyectadas`: it describes the projections, and a replay
     * writes it afresh along with them. See `db/aplicar.ts`.
     */
    this.version(10).stores({
      deshacer: '++orden, eventId',
    });

    /**
     * Two lookups by index instead of by reading sales (see `indicesDeVenta`). The transfer queue
     * read every sale the shop had made to find the few waiting: 0.7 s at 90 days of trading, on
     * the home screen and again after every sale. The check for a reference used twice read two
     * months of sales every time the payment panel opened.
     */
    this.version(11)
      .stores({
        ventas: 'id, ticket, sesionId, fecha, anulada, clienteId, enCola, [conReferencia+fecha]',
      })
      .upgrade((tx) =>
        tx
          .table('ventas')
          .toCollection()
          .modify((v: VentaLocal) => {
            Object.assign(v, indicesDeVenta(v));
          }),
      );

    // Fiado given up on (`incobrable.registrado`). A projection: rebuilt from `eventos`.
    this.version(12).stores({
      incobrables: 'id, clienteId, fecha',
    });

    /**
     * The journal added up by account and day (`SaldoDiario`). Filled here from the entries
     * already stored, so the reports read right from the first screen after the update, and in
     * a practice shop too, which never replays. On a real device `VERSION_PROYECCIONES` 4 then
     * replays the whole log at its next sync all the same: the undo notes written before this
     * know nothing of days, and taking an event back with one of them would leave its days behind.
     */
    this.version(13)
      .stores({
        saldosDiarios: 'id, fecha, [cuenta+fecha]',
      })
      .upgrade(async (tx) => {
        const dias = new Map<string, SaldoDiario>();
        await tx.table('asientos').each((a: AsientoRow) => {
          for (const l of a.lineas) {
            const id = saldoDiarioId(l.cuenta, a.fecha);
            const dia = dias.get(id) ?? { id, cuenta: l.cuenta, fecha: a.fecha, debe: 0, haber: 0, lineas: 0 };
            dia.debe += l.debe;
            dia.haber += l.haber;
            dia.lineas += 1;
            dias.set(id, dia);
          }
        });
        await tx.table('saldosDiarios').bulkPut([...dias.values()]);
      });

    // Cash taken to the bank (`efectivo.depositado`). A projection: rebuilt from `eventos`.
    this.version(14).stores({
      depositos: 'id, fecha',
    });

    // Money between two bank accounts (`banco.transferido`, 2026-10-10). A projection too.
    this.version(15).stores({
      transferenciasBanco: 'id, fecha',
    });

    // What the books refused of the log (`SinEfectoRow`, 2026-10-13): filled by the replay that
    // `VERSION_PROYECCIONES` 14 asks every device for, and kept as events are applied after it.
    this.version(16).stores({
      sinEfecto: 'eventId',
    });

    // The aguinaldo paid on its own (`aguinaldo.pagado`, 2026-10-31). A projection: rebuilt from `eventos`.
    this.version(17).stores({
      pagosAguinaldo: 'id, fecha',
    });

    // A product's prices lower for some days (`oferta.creada`, 2026-11-03). A projection: rebuilt from `eventos`.
    this.version(18).stores({
      ofertas: 'id, productoId',
    });
  }
}

/**
 * What this device adds to a sale so its indexes can find it: `enCola` while a payment waits for
 * confirmation, `conReferencia` when a payment carries a transfer reference. Written on every
 * write to `ventas` (`db/effects.ts`), and nowhere in the core: the server does not need them.
 */
export function indicesDeVenta(v: Venta): { enCola: 0 | 1; conReferencia: 0 | 1 } {
  return {
    enCola: !v.anulada && v.pagos.some((p) => p.estado === 'pendiente') ? 1 : 0,
    conReferencia: v.pagos.some((p) => Boolean(p.referencia?.trim())) ? 1 : 0,
  };
}

/** Tables wiped and rebuilt by a replay. Kept in one place so a new projection cannot be forgotten. */
export function tablasProyectadas() {
  return [
    db.usuarios,
    db.dispositivos,
    db.config,
    db.asientos,
    db.saldosDiarios,
    db.transferenciasBanco,
    db.productos,
    db.unidades,
    db.listas,
    db.precios,
    db.proveedores,
    db.existencias,
    db.lotes,
    db.compras,
    db.sesiones,
    db.ventas,
    db.saldos,
    db.recargas,
    db.reempaques,
    db.conteos,
    db.salidas,
    db.traslados,
    db.clientes,
    db.pedidos,
    db.jornadas,
    db.gastos,
    db.movimientosDueno,
    db.empleados,
    db.planillas,
    db.pagosCotizaciones,
    db.pagosAguinaldo,
    db.ofertas,
    db.abonos,
    db.incobrables,
    db.depositos,
    db.devoluciones,
    db.devolucionesCompra,
  ];
}

export const db = new AbasteceDB();

/**
 * Asks for this device's copy to be deleted on the next boot, before anything opens it.
 *
 * Deleting it on the spot means closing a database that live queries, and the sync that asked,
 * are still using, and each of them then fails on the way down. A flag and a reload let the
 * next boot do it while nothing is looking.
 */
const CLAVE_BORRAR_COPIA = 'abastece.borrarCopia';

export function pedirBorrarCopia(): void {
  try {
    sessionStorage.setItem(CLAVE_BORRAR_COPIA, '1');
  } catch {
    /* Without storage the copy survives the reload, which is how it behaved before. */
  }
}

/** Called once, before the app renders: deletes the copy if the last page asked for that. */
export async function borrarCopiaSiSePidio(): Promise<void> {
  try {
    if (sessionStorage.getItem(CLAVE_BORRAR_COPIA) !== '1') return;
    sessionStorage.removeItem(CLAVE_BORRAR_COPIA);
  } catch {
    return;
  }
  // By name, leaving `db` untouched: it has not opened yet, and opens onto an empty database.
  await Dexie.delete(db.name);
}

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export interface MetaShape {
  dispositivoId: string;
  dispositivoCodigo: string;
  token: string;
  /** Highest serverSeq this device has pulled. The sync high-water mark. */
  ultimoServerSeq: number;
  /**
   * The copy of the log pulled before this device kept the server's whole record (`sync/engine.ts`,
   * `recopiar`) has been pulled again, whole: set once. Absent, the next sync does it.
   */
  copiaCompleta: boolean;
  /** How far that second pull has gone, so a sync cut off halfway goes on from there. */
  recopiadoHasta: number;
  /**
   * Every event up to this serverSeq is applied, in the server's order, and nothing can arrive
   * before it any more. Absent: the projections have not been through `db/aplicar.ts`, and the
   * next sync replays the whole log.
   */
  aplicadoHasta: number;
  /** `VERSION_PROYECCIONES` the projections were last replayed under (see `db/aplicar.ts`). */
  proyeccionesVersion: number;
  /** Next per-device sequence number to stamp on a new event. */
  proximoDeviceSeq: number;
  /** Device clock minus server clock, in ms, as last measured. */
  clockOffsetMs: number;
  usuarioActivoId: string;
  ultimaSyncTs: string;
  /**
   * The server turned this device away: the owner revoked it in «Personal y equipos», or the
   * server no longer knows its token. The app stops until it is registered again.
   */
  dadoDeBaja: boolean;
}

export async function getMeta<K extends keyof MetaShape>(
  clave: K,
): Promise<MetaShape[K] | undefined> {
  const row = await db.meta.get(clave);
  return row?.valor as MetaShape[K] | undefined;
}

export async function setMeta<K extends keyof MetaShape>(
  clave: K,
  valor: MetaShape[K],
): Promise<void> {
  await db.meta.put({ clave, valor });
}

export async function delMeta(clave: keyof MetaShape): Promise<void> {
  await db.meta.delete(clave);
}

/**
 * Deterministic replay order.
 *
 * Sequenced events first, in the server's order. Un-acknowledged local events sort after all
 * of them, ordered among themselves by the device's own counter — so a cashier's latest edit
 * wins on their own screen, and is honestly re-ordered once the server assigns it a position.
 */
export function ordenReplay(a: LocalEvent, b: LocalEvent): number {
  const sa = a.serverSeq ?? Number.MAX_SAFE_INTEGER;
  const sb = b.serverSeq ?? Number.MAX_SAFE_INTEGER;
  if (sa !== sb) return sa - sb;
  return a.deviceSeq - b.deviceSeq;
}

/** Events still waiting to reach the server. This number is shown to the operator, always. */
export function pendientes() {
  return db.eventos.where('estado').equals('pendiente');
}
